import * as React from "react"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { LucideIcon, ExternalLink } from "lucide-react"
import { cn } from "@/lib/utils"

interface KPICardProps {
  title: string
  value: string
  subtitle: string
  icon: LucideIcon
  variant: "blue" | "red" | "green" | "yellow"
  className?: string
}

const variantStyles = {
  blue: {
    iconBg: "bg-blue-100 dark:bg-blue-900/30",
    iconColor: "text-blue-600 dark:text-blue-400",
    border: "border-l-4 border-l-blue-500",
    gradient: "bg-gradient-to-br from-blue-50/50 to-transparent dark:from-blue-950/20 dark:to-transparent",
  },
  red: {
    iconBg: "bg-red-100 dark:bg-red-900/30",
    iconColor: "text-red-600 dark:text-red-400",
    border: "border-l-4 border-l-red-500",
    gradient: "bg-gradient-to-br from-red-50/50 to-transparent dark:from-red-950/20 dark:to-transparent",
  },
  green: {
    iconBg: "bg-green-100 dark:bg-green-900/30",
    iconColor: "text-green-600 dark:text-green-400",
    border: "border-l-4 border-l-green-500",
    gradient: "bg-gradient-to-br from-green-50/50 to-transparent dark:from-green-950/20 dark:to-transparent",
  },
  yellow: {
    iconBg: "bg-yellow-100 dark:bg-yellow-900/30",
    iconColor: "text-yellow-600 dark:text-yellow-400",
    border: "border-l-4 border-l-yellow-500",
    gradient: "bg-gradient-to-br from-yellow-50/50 to-transparent dark:from-yellow-950/20 dark:to-transparent",
  },
}

export function KPICard({ title, value, subtitle, icon: Icon, variant, className }: KPICardProps) {
  const styles = variantStyles[variant]

  return (
    <Card className={cn(styles.border, styles.gradient, className)}>
      <CardHeader className="flex flex-row items-center justify-between pb-2 pt-4">
        <div className="flex items-center gap-3">
          <div className={cn("rounded-full p-2", styles.iconBg)}>
            <Icon className={cn("h-5 w-5", styles.iconColor)} />
          </div>
          <div className="text-sm font-medium text-muted-foreground">{title}</div>
        </div>
        <ExternalLink className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent className="pt-0 pb-4">
        <div className="text-3xl font-bold mb-1">{value}</div>
        <p className="text-xs text-muted-foreground">{subtitle}</p>
      </CardContent>
    </Card>
  )
}
